import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Download } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import SummaryStatsRow from '~/components/Statistics/SummaryStatsRow';
import { formatDisplayIntensity } from '~/domain/dicom/intensityUnits';
import { getDICOMDate, getDICOMTime } from '~/domain/dicom/utils';
import { LineProfile } from '~/domain/intensity/lineProfile';
import { draggingLineProfileState } from '~/state/atoms/intensityToolAtoms';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import {
    createCSVContent,
    downloadCreatedFile,
} from '~/utils/io/files/handleFileExport';
import {
    ProfileComparison,
    ProfileDataset,
    ProfileOmissions,
} from '~/domain/intensity/lineProfileDatasets';
import {
    LINE_PROFILE_CSV_HEADER,
    buildLineProfileCsvRows,
} from './lineProfileExport';
import { mmToCm } from '~/utils/units';
import LineProfileChart from './LineProfileChart';
import { toCurves } from './lineProfileCurves';

/**
 * min/max/mean/SD for one dataset in its display unit. The name (PHI → via
 * {@link SensitiveInfoTypography}) and swatch show only when curves compare.
 */
function DatasetStats({
    dataset,
    showName,
}: {
    dataset: ProfileDataset;
    showName: boolean;
}) {
    const { stats } = dataset;
    const description = dataset.label.trim();
    // Units always shown for clinical values (ui_specs).
    const format = (value: number): string =>
        formatDisplayIntensity(dataset.toDisplay(value), '');
    return (
        <Box>
            {showName && (
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mb: 0.25 }}
                >
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '2px',
                            backgroundColor: dataset.color,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {description ? (
                            <SensitiveInfoTypography>
                                {description}
                            </SensitiveInfoTypography>
                        ) : (
                            dataset.modality
                        )}
                    </Typography>
                    {description && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', flexShrink: 0 }}
                        >
                            {dataset.modality}
                        </Typography>
                    )}
                </Stack>
            )}
            <SummaryStatsRow
                stats={stats}
                format={format}
                unit={dataset.unit}
            />
        </Box>
    );
}

/**
 * Lists how many loaded datasets are missing from the chart, one line per
 * reason. Without this, a chart with fewer curves looks like a chart of every
 * dataset there is.
 */
function OmittedDatasetsNote({ omitted }: { omitted: ProfileOmissions }) {
    const intl = useIntl();
    const reasons: { id: string; count: number }[] = [
        {
            id: 'lineProfile.omittedOtherFrame',
            count: omitted.otherFrameOfReference,
        },
        {
            id: 'lineProfile.omittedUnsampleable',
            count: omitted.unsampleable,
        },
    ];
    return (
        <>
            {reasons
                .filter((reason) => reason.count > 0)
                .map((reason) => (
                    <Typography
                        key={reason.id}
                        variant="caption"
                        sx={{
                            display: 'block',
                            mt: 0.5,
                            color: 'text.disabled',
                        }}
                    >
                        {intl.formatMessage(
                            { id: reason.id },
                            { count: reason.count }
                        )}
                    </Typography>
                ))}
        </>
    );
}

export default function LineProfileDetail({
    profile,
    comparisonForProfile,
}: {
    profile: LineProfile;
    /**
     * Taken as a prop rather than from the line profile controller, which
     * also subscribes to the viewport views: a viewport layout change would
     * then redraw these charts for nothing.
     */
    comparisonForProfile: (profile: LineProfile) => ProfileComparison;
}) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const draggingId = useRecoilValue(draggingLineProfileState);

    const comparisonRef = useRef<ProfileComparison>({
        unitGroups: [],
        omitted: { otherFrameOfReference: 0, unsampleable: 0 },
    });
    const { unitGroups, omitted } = useMemo(() => {
        // While this profile's endpoint drags, reuse the last comparison
        // instead of re-sampling every dataset on each mousemove.
        if (draggingId !== null && draggingId === profile.id) {
            return comparisonRef.current;
        }
        const comparison = comparisonForProfile(profile);
        comparisonRef.current = comparison;
        return comparison;
    }, [profile, comparisonForProfile, draggingId]);
    // Memoized so the curves stay referentially stable during an endpoint
    // drag and the memoized charts skip re-layout on every mousemove.
    const curveGroups = useMemo(
        () => unitGroups.map((group) => toCurves(group)),
        [unitGroups]
    );
    // The name + swatch legend is only useful when curves compare.
    const showNames =
        unitGroups.reduce((sum, group) => sum + group.datasets.length, 0) > 1;

    // Stacked unit charts share one distance axis, so they share an x-max;
    // samples are distance-ordered, so the last one is the max.
    const xMaxCm = useMemo(() => {
        let maxMm = 0;
        for (const group of unitGroups) {
            for (const dataset of group.datasets) {
                const last = dataset.samples[dataset.samples.length - 1];
                if (last && last.distanceMm > maxMm) {
                    maxMm = last.distanceMm;
                }
            }
        }
        return maxMm > 0 ? mmToCm(maxMm) : undefined;
    }, [unitGroups]);
    // Compact stacked charts so the common image+dose pair fits unscrolled.
    const chartHeight = unitGroups.length > 1 ? 160 : 220;

    const handleExport = useCallback(() => {
        const rows = buildLineProfileCsvRows(unitGroups, showSensitiveInfo);
        downloadCreatedFile(
            createCSVContent(LINE_PROFILE_CSV_HEADER, rows),
            `line_profile_${getDICOMDate()}_${getDICOMTime()}.csv`
        );
    }, [unitGroups, showSensitiveInfo]);

    if (unitGroups.length === 0) {
        return (
            <>
                <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1 }}
                    color="text.secondary"
                >
                    {intl.formatMessage({ id: 'lineProfile.noData' })}
                </Typography>
                <OmittedDatasetsNote omitted={omitted} />
            </>
        );
    }

    return (
        <>
            {unitGroups.map((group, index) => (
                <Box key={group.datasets[0].key}>
                    <Divider sx={{ my: 0.5 }} />
                    <LineProfileChart
                        curves={curveGroups[index]}
                        unit={group.unit}
                        height={chartHeight}
                        xMaxCm={xMaxCm}
                        showDistanceAxis={index === unitGroups.length - 1}
                    />

                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {group.datasets.map((dataset) => (
                            <DatasetStats
                                key={dataset.key}
                                dataset={dataset}
                                showName={showNames}
                            />
                        ))}
                    </Stack>
                    {/* Discloses that, unlike cursor/DVH, no display dose
                        scale is applied. */}
                    {group.datasets.some(
                        (dataset) => dataset.modality === 'RTDOSE'
                    ) && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mt: 0.5,
                                color: 'text.disabled',
                            }}
                        >
                            {intl.formatMessage({
                                id: 'lineProfile.doseUnscaled',
                            })}
                        </Typography>
                    )}
                </Box>
            ))}
            <OmittedDatasetsNote omitted={omitted} />
            <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Download size={15} />}
                onClick={handleExport}
                fullWidth
                sx={{ mt: 1 }}
            >
                {intl.formatMessage({ id: 'lineProfile.export' })}
            </Button>
        </>
    );
}
